import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/http.js';
import { useAuth } from '../hooks/useAuth.jsx';

/**
 * Página de retorno pós-pagamento Stripe (embedded checkout).
 * URL: /billing/return?session_id=cs_xxx
 *
 * 1. Confirma o status da sessão Stripe via GET /billing/session-status
 * 2. Se completa, chama POST /billing/sync-session para ativar a assinatura
 *    no banco imediatamente (fallback caso o webhook ainda não tenha processado)
 * 3. Exibe feedback e redireciona para /billing
 */
export function BillingReturn() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshSession } = useAuth();
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error' | 'unavailable'
    const [planInfo, setPlanInfo] = useState(null);

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (!sessionId) {
            setTimeout(() => setStatus('error'), 0);
            return;
        }

        apiClient
            .request(`/billing/session-status?session_id=${encodeURIComponent(sessionId)}`)
            .then(async ({ res, data }) => {
                // 502/5xx: o Stripe (ou a API) está indisponível — não é "pagamento recusado".
                if (res.status >= 500) {
                    setStatus('unavailable');
                    return;
                }
                if (!res.ok || data?.status !== 'complete') {
                    setStatus('error');
                    return;
                }

                // Ativa a assinatura diretamente (não espera apenas o webhook)
                try {
                    const { res: syncRes, data: syncData } = await apiClient.request(
                        `/billing/sync-session?session_id=${encodeURIComponent(sessionId)}`,
                        { method: 'POST' }
                    );
                    if (syncRes.ok && syncData?.activated) {
                        setPlanInfo(syncData);
                    }
                } catch {
                    // Se sync falhar, o webhook vai resolver; seguimos com sucesso de qualquer forma
                }

                // Atualiza o contexto de auth para refletir o novo plano em toda a app
                await refreshSession().catch(() => null);
                setStatus('success');
            })
            .catch(() => setStatus('error'));
    }, [searchParams]);

    const handleContinue = () => navigate('/billing');

    return (
        <div className="min-h-screen flex items-center justify-center bg-surface p-6">
            <div className="bg-surface-container-low rounded-2xl shadow-xl border border-outline-variant w-full max-w-md p-8 text-center">
                {status === 'loading' && (
                    <>
                        <div className="text-4xl mb-4">⏳</div>
                        <h1 className="text-xl font-bold mb-2">Confirmando pagamento...</h1>
                        <p className="text-on-surface-variant text-sm">Aguarde um instante.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="text-5xl mb-4">✅</div>
                        <h1 className="text-2xl font-bold mb-2">Assinatura confirmada!</h1>
                        <p className="text-on-surface-variant text-sm mb-6">
                            {planInfo
                                ? `Plano ${planInfo.plan_id} ativado com ${planInfo.credits_per_week} créditos/semana.`
                                : 'Seu plano foi ativado. Bem-vindo ao CatholicGPT!'}
                        </p>
                        <button
                            onClick={handleContinue}
                            className="w-full bg-primary text-on-primary font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity"
                        >
                            Ir para meu painel
                        </button>
                    </>
                )}

                {status === 'unavailable' && (
                    <>
                        <div className="text-5xl mb-4">⚠️</div>
                        <h1 className="text-2xl font-bold mb-2">Não conseguimos confirmar agora</h1>
                        <p className="text-on-surface-variant text-sm mb-6">
                            O serviço de pagamento está temporariamente indisponível. Se o valor foi cobrado,
                            sua assinatura será ativada automaticamente em instantes.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-primary text-on-primary font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity mb-3"
                        >
                            Tentar novamente
                        </button>
                        <button
                            onClick={handleContinue}
                            className="w-full bg-surface-container-highest text-on-surface font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity"
                        >
                            Ir para meu painel
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="text-5xl mb-4">❌</div>
                        <h1 className="text-2xl font-bold mb-2">Pagamento não concluído</h1>
                        <p className="text-on-surface-variant text-sm mb-6">
                            Não foi possível confirmar seu pagamento. Se o valor foi cobrado,
                            entre em contato com o suporte.
                        </p>
                        <button
                            onClick={handleContinue}
                            className="w-full bg-surface-container-highest text-on-surface font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity"
                        >
                            Voltar para planos
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
